import Tooltip from '@mui/material/Tooltip';
import { Ellipsis } from './ui/Ellipsis';
import { trim } from 'utils/string';
import useResizeAware from 'react-resize-aware';
import { useCopyableContent } from 'hooks/useCopyableContent';
import { Icon } from 'common/ui/Icon';

type Props = {
  css?: string;
  name: string;
  /** Leading portion of `name` that gets a hover "Click to Copy" tooltip and copies on click. */
  copyableText?: string | null;
  conditionalTooltip?: boolean;
  customEllipsisCss?: unknown | null;
  ['data-testid']?: string;
  variant?: 'default' | 'noWrap';
};

const TextWithTooltip = ({
  name,
  copyableText = null,
  conditionalTooltip = false,
  customEllipsisCss = null,
  'data-testid': dataTestid,
  variant = 'default',
}: Props): React.ReactElement => {
  // @ts-expect-error [EN-10263] - TS7006 - Parameter 'target' implicitly has an 'any' type.
  const [resizeListener, sizes] = useResizeAware((target) => ({
    clientWidth: target?.parentElement?.clientWidth ?? 0,
    scrollWidth: target?.parentElement?.scrollWidth ?? 0,
  }));

  const isOverflowing =
    sizes != null
      ? (sizes as unknown as { scrollWidth: number; clientWidth: number }).scrollWidth >
        (sizes as unknown as { scrollWidth: number; clientWidth: number }).clientWidth
      : false;

  const tooltipSlotProps =
    variant === 'noWrap' ? { tooltip: { sx: { maxWidth: 'none' } } } : undefined;

  // Only split when the copyable text is actually the start of `name`
  const hasCopyableSegment =
    copyableText != null && copyableText !== '' && name.startsWith(copyableText);

  return (
    <Ellipsis
      data-privacy
      css={{
        minWidth: 0,
        display: 'inline-block',
        padding: 0,
        position: 'relative',
        ...(customEllipsisCss as React.CSSProperties),
      }}
      data-testid={dataTestid}
      data-value={trim(name)}
    >
      {resizeListener}
      {hasCopyableSegment ? (
        <>
          <CopyableSegment text={copyableText} tooltipText={name} slotProps={tooltipSlotProps} />
          <Tooltip
            title={name}
            disableHoverListener={conditionalTooltip === true && !isOverflowing}
            slotProps={tooltipSlotProps}
          >
            <span>{name.slice(copyableText.length)}</span>
          </Tooltip>
        </>
      ) : (
        <Tooltip
          title={name}
          disableHoverListener={conditionalTooltip === true && !isOverflowing}
          slotProps={tooltipSlotProps}
        >
          <span>{name}</span>
        </Tooltip>
      )}
    </Ellipsis>
  );
};

// Own component so the copy hook's state only exists when a segment is copyable
const CopyableSegment = ({
  text,
  tooltipText,
  slotProps,
}: {
  text: string;
  tooltipText: string;
  slotProps?: React.ComponentProps<typeof Tooltip>['slotProps'];
}): React.ReactElement => {
  const { copyOnClick, copied } = useCopyableContent({ value: text });

  return (
    <Tooltip
      slotProps={slotProps}
      title={
        <>
          <div>{tooltipText}</div>
          <span css="display: flex; align-items: center; gap: 4px; font-size: 10px;">
            <Icon name={copied ? 'check' : 'file_copy'} css="height: 12px; width: 12px;" />
            {copied ? 'Copied' : 'Click to Copy Exam Description'}
          </span>
        </>
      }
    >
      <button
        type="button"
        aria-label={`Copy exam description: ${text}`}
        css="background: transparent; border: 0; color: inherit; cursor: pointer; font: inherit; padding: 0;"
        onClick={copyOnClick}
        data-testid="copyable-text"
      >
        {text}
      </button>
    </Tooltip>
  );
};

export default TextWithTooltip;
